export interface V3 {
  x: number;
  y: number;
  z: number;
}

export const v3 = (x = 0, y = 0, z = 0): V3 => ({ x, y, z });
export const ZERO: V3 = Object.freeze(v3());
export const UP: V3 = Object.freeze(v3(0, 1, 0));
export const DOWN: V3 = Object.freeze(v3(0, -1, 0));

export const add = (a: V3, b: V3): V3 => v3(a.x + b.x, a.y + b.y, a.z + b.z);
export const sub = (a: V3, b: V3): V3 => v3(a.x - b.x, a.y - b.y, a.z - b.z);
export const scale = (a: V3, s: number): V3 => v3(a.x * s, a.y * s, a.z * s);
export const dot = (a: V3, b: V3): number => a.x * b.x + a.y * b.y + a.z * b.z;
export const cross = (a: V3, b: V3): V3 =>
  v3(a.y * b.z - a.z * b.y, a.z * b.x - a.x * b.z, a.x * b.y - a.y * b.x);
export const lengthSq = (a: V3): number => dot(a, a);
export const length = (a: V3): number => Math.sqrt(dot(a, a));
export const distance = (a: V3, b: V3): number => length(sub(a, b));
export const horizontal = (a: V3): V3 => v3(a.x, 0, a.z);
export const neg = (a: V3): V3 => v3(-a.x, -a.y, -a.z);

export function normalizeOrZero(a: V3): V3 {
  const len = length(a);
  return len > 1e-9 ? scale(a, 1 / len) : v3();
}

/** Forward for a yaw, matching Three.js `rotation.y = yaw` on a model facing -z. */
export const forwardFromYaw = (yaw: number): V3 =>
  v3(-Math.sin(yaw), 0, -Math.cos(yaw));
export const rightFromYaw = (yaw: number): V3 =>
  v3(Math.cos(yaw), 0, -Math.sin(yaw));
export const yawFromDirection = (d: V3): number => Math.atan2(-d.x, -d.z);
